import TrimbleMaps from '@trimblemaps/trimblemaps-js';

import { MapLayers } from './enums';

/**
 * Using an incoming isVisible boolean, this will return the required layer visibility term used in the Trimble Maps layers.
 * @param isVisible Indicator for if the layer should be visible.
 */
export const isVisibleUtil = (isVisible: boolean): 'visible' | 'none' => {
    const visibility = isVisible ? 'visible' : 'none';
    return visibility;
};

/**
 * Toggles the visibility property of the layer given on the map.
 * @param map Instance of your map
 * @param layerName Specific Map Layer you want to toggle the visibility on
 * @param isVisible Indicator on whether the layer should be visible or not
 */
export const toggleLayerVisibility = (map: TrimbleMaps.Map | null, layerName: MapLayers, isVisible: boolean): void => {
    if (map) {
        // Toggle a specific layers visibilty based on the state that is passed in
        const visibility = isVisibleUtil(isVisible);

        // first make sure that the layer exists first, as an extra safeguard
        if (map.getLayer(layerName) !== undefined) {
            map.setLayoutProperty(layerName, 'visibility', visibility);
        }
    }
};

/**
 * Triggers the fitBounds method on the map in order to zoom the map into the set of bounds given.
 * @param map Instance of your map
 * @param bounds Set of bounds as determined by your various layers (new TrimbleMaps.LngLatBounds()).
 * @param zoomMap Indicator on whether the map should be zoomed or not.
 * @param padding Padding on the map to set on the fitBounds method. This is optional.
 * @param maxZoom The maximum amount of zoom to stop the map from zooming in too far. This is optional.
 */
export const fitMapToBounds = ({
    map,
    bounds,
    zoomMap,
    padding = {
        top: 45,
        bottom: 45,
        left: 80,
        right: 80,
    },
    maxZoom,
}: {
    map: TrimbleMaps.Map;
    bounds: TrimbleMaps.LngLatBoundsLike;
    zoomMap: boolean;
    padding?: {
        top: number;
        bottom: number;
        left: number;
        right: number;
    };
    maxZoom?: number;
}): void => {
    const options: TrimbleMaps.FitBoundsOptions = {
        padding,
        ...(maxZoom !== undefined) && {
            maxZoom,
        },
    };

    if (zoomMap) {
        map.fitBounds(bounds, options);
    }
};

/**
 * Create an instance of a popup to be used in the mouse event functions
 */
export const trimbleMapsPopup = new TrimbleMaps.Popup({
    offset: {
        bottom: [0, -20],
        left: [20, 0],
        right: [-20, 0],
        top: [0, 20],
    },
    maxWidth: '280px', // default is '240px'
    closeButton: false,
    closeOnClick: false,
});

/**
 * Changes the cursor on the map to a pointer
 * @param map Instance of your map
 */
export const handleMouseEnter = (map: TrimbleMaps.Map | null): void => {
    if (map) {
        // Change the cursor to a pointer when the it enters a feature in the layer.
        map.getCanvas().style.cursor = 'pointer';
    }
};

/**
 * Sets the mouse cursor back to the default
 * @param map Instance of your map
 */
export const handleMouseLeave = (map: TrimbleMaps.Map | null): void => {
    if (map) {
        map.getCanvas().style.cursor = '';
    }
};

/**
 * Displays a popup when hovering over a LineString layer that has a mouseenter event added
 * @param map Instance of your map
 * @param mapLayer Specific Map Layer you want to show the popup on
 * @param e Event of the mouseenter
 * @param popup A TrimbleMaps.Popup that will be shown
 */
export const handleLineStringPopupMouseEnter = (map: TrimbleMaps.Map | null, mapLayer: MapLayers, e: any, popup: TrimbleMaps.Popup): void => {
    if (map) {
        // Get the layer that was hovered
        const features = map.queryRenderedFeatures(e.point);
        const currentFeature = features[0];

        // If it was this layer, fire the hover function
        if (currentFeature.layer.id === mapLayer) {
            handleMouseEnter(map);
            const { description } = e.features[0].properties;

            // Populate the popup and set its coordinates based on the location of the mouse event.
            popup
                .setLngLat(e.lngLat)
                .setHTML(description)
                .addTo(map);
        }
    }
};

/**
 * Displays a popup with the given description when hovering over a LineString layer that has a mouseenter event added
 * Use when the map logic is handling most of the logic done in `handleLineStringPopupMouseEnter`
 * @param map Instance of your map
 * @param e Event of the mouseenter
 * @param description HTML to display in the popup
 * @param popup A TrimbleMaps.Popup that will be shown
 */
export const handleLineStringPopupWithDescriptionMouseEnter = (map: TrimbleMaps.Map | null, e: any, description: string, popup: TrimbleMaps.Popup): void => {
    if (map) {
        handleMouseEnter(map);

        // Populate the popup and set its coordinates based on the location of the mouse event.
        popup
            .setLngLat(e.lngLat)
            .setHTML(description)
            .addTo(map);
    }
};

/**
 * Displays a popup when hovering over a layer that has a mouseenter event added
 * @param map Instance of your map
 * @param e Event of the mouseenter
 * @param popup A TrimbleMaps.Popup that will be shown
 */
export const handleFillPopupMouseEnter = (map: TrimbleMaps.Map | null, e: any, popup: TrimbleMaps.Popup): void => {
    if (map) {
        handleMouseEnter(map);
        const coordinates = e.lngLat;
        const { description } = e.features[0].properties;

        // Populate the popup and set its coordinates based on the feature found.
        popup
            .setLngLat(coordinates)
            .setHTML(description)
            .addTo(map);
    }
};

/**
 * Displays a popup when hovering over a layer that has a mouseenter event added
 * @param map Instance of your map
 * @param e Event of the mouseenter
 * @param popup A TrimbleMaps.Popup that will be shown
 */
export const handlePopupMouseEnter = (map: TrimbleMaps.Map | null, e: any, popup: TrimbleMaps.Popup): void => {
    if (map) {
        handleMouseEnter(map);
        const coordinates = e.features[0].geometry.coordinates.slice();
        const { description } = e.features[0].properties;

        // Ensure that if the map is zoomed out such that multiple copies of the feature
        // are visible, the popup appears over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        // Populate the popup and set its coordinates based on the feature found.
        popup
            .setLngLat(coordinates)
            .setHTML(description)
            .addTo(map);
    }
};

/**
 * Removes the popup when the mouseleave event is fired on the layer
 * @param map Instance of your map
 * @param popup A TrimbleMaps.Popup that will be hidden
 */
export const handlePopupMouseLeave = (map: TrimbleMaps.Map | null, popup: TrimbleMaps.Popup): void => {
    handleMouseLeave(map);
    popup.remove();
};
